import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  addInternalNote,
  getConversation,
  listConversations,
  replyToConversation,
  returnConversationToAgent,
  setConversationStatus,
  takeOverConversation,
} from './api'
import type { DeskConversationFilter } from './types'

export const deskQueryKey = ['desk'] as const

/**
 * Intervalos de polling do Desk (interim "quase tempo real" até a migração para websocket). O React Query só
 * refaz a busca enquanto a query está montada; com `refetchIntervalInBackground` desligado (padrão), não há
 * polling quando a aba está oculta — nada de carga desperdiçada. A conversa aberta atualiza mais rápido que a
 * lista, pois é o que o atendente está olhando.
 */
const ConversationListPollMs = 15_000
const OpenConversationPollMs = 10_000

export function conversationsQueryOptions(tenantId: string, filter: DeskConversationFilter) {
  return queryOptions({
    queryKey: ['desk', tenantId, 'conversations', filter] as const,
    queryFn: () => listConversations(tenantId, filter),
    // Novas conversas aparecem sozinhas na lista enquanto o Desk está aberto (polling; ver constantes acima).
    staleTime: 10_000,
    refetchInterval: ConversationListPollMs,
  })
}

export function conversationQueryOptions(tenantId: string, conversationId: number) {
  return queryOptions({
    queryKey: ['desk', tenantId, 'conversation', conversationId] as const,
    queryFn: () => getConversation(tenantId, conversationId),
    // Mensagens novas (do cliente ou do agente) aparecem sozinhas na timeline da conversa aberta.
    staleTime: 10_000,
    refetchInterval: OpenConversationPollMs,
  })
}

export function useConversations(tenantId: string | undefined, filter: DeskConversationFilter) {
  return useQuery({
    ...conversationsQueryOptions(tenantId ?? '', filter),
    enabled: tenantId !== undefined,
  })
}

export function useConversation(tenantId: string | undefined, conversationId: number | null) {
  return useQuery({
    ...conversationQueryOptions(tenantId ?? '', conversationId ?? 0),
    enabled: tenantId !== undefined && conversationId !== null,
  })
}

/**
 * Enviar uma resposta PUBLICA (acao humana). Ao concluir, revalida a conversa aberta e a lista, para a
 * mensagem enviada aparecer na timeline sem recarregar a pagina.
 */
export function useReplyToConversation(tenantId: string, conversationId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (content: string) => replyToConversation(tenantId, conversationId, content),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['desk', tenantId, 'conversation', conversationId],
      })
      await queryClient.invalidateQueries({ queryKey: ['desk', tenantId, 'conversations'] })
    },
  })
}

/** Adicionar uma nota interna (privada). Revalida a conversa aberta. */
export function useAddInternalNote(tenantId: string, conversationId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (content: string) => addInternalNote(tenantId, conversationId, content),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['desk', tenantId, 'conversation', conversationId],
      })
    },
  })
}

/** Resolver/reabrir a conversa. Revalida a conversa aberta e a lista (o status muda em ambas). */
export function useSetConversationStatus(tenantId: string, conversationId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (action: 'resolve' | 'reopen') =>
      setConversationStatus(tenantId, conversationId, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['desk', tenantId, 'conversation', conversationId],
      })
      await queryClient.invalidateQueries({ queryKey: ['desk', tenantId, 'conversations'] })
    },
  })
}

/**
 * Ações sobre o chamado (atendimento humano): assumir a conversa (pausa a IA) ou devolvê-la para a IA. Revalidam a
 * conversa aberta, a lista e a presença (a contagem de chamados aguardando muda).
 */
export function useTicketAction(tenantId: string, conversationId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (action: 'take-over' | 'return-to-agent') =>
      action === 'take-over'
        ? takeOverConversation(tenantId, conversationId)
        : returnConversationToAgent(tenantId, conversationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['desk', tenantId, 'conversation', conversationId],
      })
      await queryClient.invalidateQueries({ queryKey: ['desk', tenantId, 'conversations'] })
      await queryClient.invalidateQueries({ queryKey: ['presence', tenantId] })
    },
  })
}
